/**
 * web-studio-sites D4, D14 — the pieces this kit can place: each one's contract,
 * its behavior half and its standard look, kept apart so a site's own drawing
 * (D7) can stand in for the look while the behavior half runs as usual. Every
 * plug-in's placeable pieces join here; the plug-in check reads the studio's
 * switches, and a piece whose contract is not `placeable` is never mounted.
 */
import type { ComponentType } from 'react';

import type { PieceContract } from '../contract/define';
import { KIT_CONTENT_WIDTH_PIECES } from '../contract/kit';
import type { RequiredParts } from '../contract/required';
import type { PieceRuntime } from '../contract/runtime';
import { AccountView } from '../pieces/account/AccountView';
import { contract as account } from '../pieces/account/contract';
import { useAccount } from '../pieces/account/useAccount';
import { BookingColumnView } from '../pieces/booking-column/BookingColumnView';
import { contract as bookingColumn } from '../pieces/booking-column/contract';
import { requiredParts as bookingColumnRequired } from '../pieces/booking-column/requiredParts';
import { useBookingColumn } from '../pieces/booking-column/useBookingColumn';
import { BookingsListView } from '../pieces/bookings-list/BookingsListView';
import { contract as bookingsList } from '../pieces/bookings-list/contract';
import { requiredParts as bookingsListRequired } from '../pieces/bookings-list/requiredParts';
import { useBookingsList } from '../pieces/bookings-list/useBookingsList';
import { ComingUpView } from '../pieces/coming-up/ComingUpView';
import { contract as comingUp } from '../pieces/coming-up/contract';
import { requiredParts as comingUpRequired } from '../pieces/coming-up/requiredParts';
import { useComingUp } from '../pieces/coming-up/useComingUp';
import { EmailSignupView } from '../pieces/email-signup/EmailSignupView';
import { contract as emailSignup } from '../pieces/email-signup/contract';
import { useEmailSignup } from '../pieces/email-signup/useEmailSignup';
import { EventsGridView } from '../pieces/events-grid/EventsGridView';
import { contract as eventsGrid } from '../pieces/events-grid/contract';
import { requiredParts as eventsGridRequired } from '../pieces/events-grid/requiredParts';
import { useEventsGrid } from '../pieces/events-grid/useEventsGrid';
import { EventsRowView } from '../pieces/events-row/EventsRowView';
import { contract as eventsRow } from '../pieces/events-row/contract';
import { requiredParts as eventsRowRequired } from '../pieces/events-row/requiredParts';
import { useEventsRow } from '../pieces/events-row/useEventsRow';
import { GuideView } from '../pieces/guide/GuideView';
import { contract as guide } from '../pieces/guide/contract';
import { useGuide } from '../pieces/guide/useGuide';
import { MessageStudioView } from '../pieces/message-studio/MessageStudioView';
import { contract as messageStudio } from '../pieces/message-studio/contract';
import { useMessageStudio } from '../pieces/message-studio/useMessageStudio';
import { NextSessionView } from '../pieces/next-session/NextSessionView';
import { contract as nextSession } from '../pieces/next-session/contract';
import { requiredParts as nextSessionRequired } from '../pieces/next-session/requiredParts';
import { useNextSession } from '../pieces/next-session/useNextSession';
import { OfferingsView } from '../pieces/offerings/OfferingsView';
import { contract as offerings } from '../pieces/offerings/contract';
import { requiredParts as offeringsRequired } from '../pieces/offerings/requiredParts';
import { useOfferings } from '../pieces/offerings/useOfferings';
import { OpenTimesView } from '../pieces/open-times/OpenTimesView';
import { contract as openTimes } from '../pieces/open-times/contract';
import { requiredParts as openTimesRequired } from '../pieces/open-times/requiredParts';
import { useOpenTimes } from '../pieces/open-times/useOpenTimes';
import { contract as shopBag } from '../pieces/shop-bag/contract';
import { requiredParts as shopBagRequired } from '../pieces/shop-bag/requiredParts';
import { ShopBagView } from '../pieces/shop-bag/ShopBagView';
import { useShopBag } from '../pieces/shop-bag/useShopBag';
import { contract as shopBagButton } from '../pieces/shop-bag-button/contract';
import { requiredParts as shopBagButtonRequired } from '../pieces/shop-bag-button/requiredParts';
import { ShopBagButtonView } from '../pieces/shop-bag-button/ShopBagButtonView';
import { useShopBagButton } from '../pieces/shop-bag-button/useShopBagButton';
import { contract as shopGrid } from '../pieces/shop-grid/contract';
import { requiredParts as shopGridRequired } from '../pieces/shop-grid/requiredParts';
import { ShopGridView } from '../pieces/shop-grid/ShopGridView';
import { useShopGrid } from '../pieces/shop-grid/useShopGrid';
import { contract as shopProduct } from '../pieces/shop-product/contract';
import { requiredParts as shopProductRequired } from '../pieces/shop-product/requiredParts';
import { ShopProductView } from '../pieces/shop-product/ShopProductView';
import { useShopProduct } from '../pieces/shop-product/useShopProduct';
import { ServicesView } from '../pieces/services/ServicesView';
import { SiteFormView } from '../pieces/site-form/SiteFormView';
import { contract as siteForm } from '../pieces/site-form/contract';
import { useSiteForm } from '../pieces/site-form/useSiteForm';
import { contract as services } from '../pieces/services/contract';
import { requiredParts as servicesRequired } from '../pieces/services/requiredParts';
import { useServices } from '../pieces/services/useServices';
import { StoryView } from '../pieces/story/StoryView';
import { contract as story } from '../pieces/story/contract';
import { useStory } from '../pieces/story/useStory';
import { WaitingOnYouView } from '../pieces/waiting-on-you/WaitingOnYouView';
import { contract as waitingOnYou } from '../pieces/waiting-on-you/contract';
import { requiredParts as waitingOnYouRequired } from '../pieces/waiting-on-you/requiredParts';
import { useWaitingOnYou } from '../pieces/waiting-on-you/useWaitingOnYou';

/** Any piece's runtime, as the kit handles it without knowing which piece. */
export type AnyRuntime = PieceRuntime<PieceContract, unknown, unknown>;

/** What the kit hands a piece's hook: the placeholder's raw options. */
export interface KitInput {
  readonly options?: Readonly<Record<string, unknown>>;
}

export interface KitPiece {
  readonly contract: PieceContract;
  /** The behavior half: the piece's hook, given the placeholder's options. */
  readonly use: (input: KitInput) => AnyRuntime;
  /** The standard look. */
  readonly View: ComponentType<AnyRuntime>;
  /** What the current state requires a site's drawing to show (D7). */
  readonly requiredParts: RequiredParts<unknown>;
  /** How its frame is sized (D4): the placeholder's width, or its own content's for a piece made for a header row. */
  readonly fit: 'placeholder' | 'content';
}

const nothingRequired: RequiredParts<unknown> = () => [];

/** D4: the pieces made to sit in a header row, whose frame takes their content's width (contract/kit.ts). */
const CONTENT_WIDTH: ReadonlySet<string> = new Set(KIT_CONTENT_WIDTH_PIECES);

/** Binds a piece's hook, look and required parts, checking that they take what the hook returns. */
function kitPiece<S, R extends { readonly state: S }>(
  contract: PieceContract,
  use: (input: KitInput) => R,
  View: ComponentType<R>,
  requiredParts: RequiredParts<S> = nothingRequired,
): KitPiece {
  return {
    contract,
    use: use as unknown as KitPiece['use'],
    View: View as unknown as KitPiece['View'],
    requiredParts: requiredParts as RequiredParts<unknown>,
    fit: CONTENT_WIDTH.has(contract.name) ? 'content' : 'placeholder',
  };
}

const ALL: readonly KitPiece[] = [
  kitPiece(services, useServices, ServicesView, servicesRequired),
  kitPiece(eventsRow, useEventsRow, EventsRowView, eventsRowRequired),
  kitPiece(eventsGrid, useEventsGrid, EventsGridView, eventsGridRequired),
  kitPiece(openTimes, useOpenTimes, OpenTimesView, openTimesRequired),
  kitPiece(bookingColumn, useBookingColumn, BookingColumnView, bookingColumnRequired),
  kitPiece(story, useStory, StoryView),
  kitPiece(guide, useGuide, GuideView),
  kitPiece(nextSession, useNextSession, NextSessionView, nextSessionRequired),
  kitPiece(waitingOnYou, useWaitingOnYou, WaitingOnYouView, waitingOnYouRequired),
  kitPiece(comingUp, useComingUp, ComingUpView, comingUpRequired),
  kitPiece(bookingsList, useBookingsList, BookingsListView, bookingsListRequired),
  kitPiece(account, useAccount, AccountView),
  kitPiece(messageStudio, useMessageStudio, MessageStudioView),
  kitPiece(offerings, useOfferings, OfferingsView, offeringsRequired),
  // studio-shop D4: the shop's pieces. The grid opens the page's product window, and the bag a page places is a drawer.
  kitPiece(shopGrid, useShopGrid, ShopGridView, shopGridRequired),
  kitPiece(shopProduct, useShopProduct, ShopProductView, shopProductRequired),
  kitPiece(shopBag, useShopBag, ShopBagView, shopBagRequired),
  kitPiece(shopBagButton, useShopBagButton, ShopBagButtonView, shopBagButtonRequired),
  // form-embed D8: the forms plug-in's piece, mounted only where the studio has it on (standard-only).
  kitPiece(siteForm, useSiteForm, SiteFormView),
  // email-signup D5: the email list plug-in's box, mounted only where the studio has it on (standard-only).
  kitPiece(emailSignup, useEmailSignup, EmailSignupView),
];

/** The kit's pieces by name. */
export const KIT_PIECES: ReadonlyMap<string, KitPiece> = new Map(ALL.map((p) => [p.contract.name, p]));
